// Analytics helper module for PetalPro
// Provides a clean interface for tracking goals, events and revenue

const Analytics = {
  // Core tracking function that interfaces with the tracking script
  track(goalName, props = {}) {
    if (window.rybbit && typeof window.rybbit.event === 'function') {
      window.rybbit.event(goalName, props);
    }
  },

  // Track a conversion goal
  trackGoal(goalName, metadata = {}) {
    const props = {
      path: window.location.pathname,
      ...metadata
    };
    this.track(goalName, props);
  },

  // Track a custom event with additional properties
  trackEvent(eventName, properties = {}) {
    this.track(eventName, properties);
  },

  // Track revenue events (for subscription/payment tracking)
  trackRevenue(goalName, amount, currency = 'USD', additionalProps = {}) {
    const props = {
      revenue_amount: amount,
      revenue_currency: currency,
      ...additionalProps
    };
    this.track(goalName, props);
  },

  // Track 404 errors
  track404() {
    this.track('404', { path: window.location.pathname });
  },

  // Track outbound link clicks
  trackOutboundLink(url) {
    this.track('Outbound Link: Click', { url });
  },

  // Track file downloads
  trackDownload(filename) {
    this.track('File Download', { filename });
  },

  // Track search queries
  trackSearch(query, resultsCount) {
    this.track('Site Search', { query, results: resultsCount });
  },

  // Track form abandonment
  trackFormAbandonment(formName, lastField) {
    this.track('Form Abandoned', { form: formName, lastField });
  },

  // Track scroll depth (useful for blog posts)
  trackScrollDepth(depth) {
    this.track('Scroll Depth', { depth: `${depth}%` });
  },

  // Initialize auto-tracking for elements with data attributes
  initAutoTracking() {
    // Auto-track form submissions with data-track attribute
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.dataset.track) {
        const props = {
          path: window.location.pathname
        };
        
        // Add form-specific props if specified
        if (form.dataset.trackProps) {
          try {
            Object.assign(props, JSON.parse(form.dataset.trackProps));
          } catch (error) {
            console.error('Invalid JSON in data-track-props:', error);
          }
        }
        
        this.trackGoal(form.dataset.track, props);
      }
    });

    // Auto-track clicks with data-track-click attribute
    document.addEventListener('click', (e) => {
      const clickable = e.target.closest('[data-track-click]');
      if (clickable) {
        const props = {
          path: window.location.pathname,
          text: clickable.textContent.trim().substring(0, 50) // First 50 chars of text
        };
        
        // Add click-specific props if specified
        if (clickable.dataset.trackProps) {
          try {
            Object.assign(props, JSON.parse(clickable.dataset.trackProps));
          } catch (error) {
            console.error('Invalid JSON in data-track-props:', error);
          }
        }
        
        this.trackGoal(clickable.dataset.trackClick, props);
      }
    });

    // Outbound links are reported by the tracking script itself.

    // Initialize CTA tracking on page load and mutations
    this.initCTATracking();
  },

  // Initialize tracking for CTA blocks
  initCTATracking() {
    const enhanceCTAs = () => {
      document.querySelectorAll('[data-cta-block]').forEach(cta => {
        // Skip if already enhanced
        if (cta.dataset.ctaEnhanced === 'true') return;
        
        const trackingId = cta.dataset.ctaTrackingId || 'cta-default';
        const postSlug = cta.dataset.postSlug || '';
        const buttons = cta.querySelectorAll('button, a[href]');
        
        buttons.forEach(element => {
          // Don't override existing tracking
          if (!element.dataset.trackClick) {
            element.dataset.trackClick = 'Blog CTA Clicked';
            element.dataset.trackProps = JSON.stringify({
              cta_variant: trackingId,
              cta_element: element.tagName.toLowerCase(),
              cta_text: element.textContent.trim().substring(0, 50),
              cta_url: element.href || '',
              post_slug: postSlug
            });
          }
        });
        
        // Mark as enhanced
        cta.dataset.ctaEnhanced = 'true';
      });
    };

    // Enhance CTAs on initial load
    enhanceCTAs();

    // Watch for dynamically added CTAs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          enhanceCTAs();
        }
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
};

// Common goal names for consistency across the application
export const Goals = {
  // User lifecycle
  REGISTRATION_STARTED: 'Registration Started',
  USER_REGISTERED: 'User Registered',
  USER_LOGGED_IN: 'User Logged In',
  USER_ONBOARDED: 'User Onboarded',
  
  // Newsletter
  NEWSLETTER_SIGNUP: 'Newsletter Signup',
  NEWSLETTER_CONFIRMED: 'Newsletter Confirmed',
  NEWSLETTER_UNSUBSCRIBED: 'Newsletter Unsubscribed',
  
  // Contact/Support
  CONTACT_FORM_SUBMITTED: 'Contact Form Submitted',
  SUPPORT_TICKET_CREATED: 'Support Ticket Created',
  
  // Subscription/Billing
  TRIAL_STARTED: 'Trial Started',
  SUBSCRIPTION_CREATED: 'Subscription Created',
  SUBSCRIPTION_UPGRADED: 'Subscription Upgraded',
  SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',
  PAYMENT_SUCCEEDED: 'Payment Succeeded',
  PAYMENT_FAILED: 'Payment Failed',
  
  // Organization
  ORG_CREATED: 'Organization Created',
  ORG_MEMBER_INVITED: 'Organization Member Invited',
  ORG_MEMBER_JOINED: 'Organization Member Joined',
  
  // Feature Usage
  FEATURE_USED: 'Feature Used',
  API_KEY_CREATED: 'API Key Created',
  EXPORT_COMPLETED: 'Export Completed',
  
  // Content Engagement
  ARTICLE_READ: 'Article Read',
  VIDEO_WATCHED: 'Video Watched',
  DOCUMENTATION_VIEWED: 'Documentation Viewed',
  
  // Landing Page Engagement
  LANDING_PAGE_VIEWED: 'Landing Page Viewed',
  LANDING_PAGE_SCROLL_25: 'Landing Page Scroll 25%',
  LANDING_PAGE_SCROLL_50: 'Landing Page Scroll 50%',
  LANDING_PAGE_SCROLL_75: 'Landing Page Scroll 75%',
  LANDING_PAGE_COMPLETED: 'Landing Page Completed',
  LANDING_PAGE_BOUNCED: 'Landing Page Bounced',
  
  // Blog Post Engagement
  BLOG_POST_VIEWED: 'Blog Post Viewed',
  BLOG_POST_READ_25: 'Blog Post Read 25%',
  BLOG_POST_READ_50: 'Blog Post Read 50%',
  BLOG_POST_READ_75: 'Blog Post Read 75%',
  BLOG_POST_COMPLETED: 'Blog Post Completed',
  BLOG_POST_READ: 'Blog Post Read',
  BLOG_POST_BOUNCED: 'Blog Post Bounced',
  
  // CTAs
  CTA_CLICKED: 'CTA Clicked',
  PRICING_PAGE_VIEWED: 'Pricing Page Viewed',
  DEMO_REQUESTED: 'Demo Requested'
};

// Export for use in other modules
export default Analytics;